import {
  defaultSitePaginationConfig,
  type CDLQueryParams,
  type RecordList,
  type Site,
  type RecordEntityId,
  type DeliveryDestination,
  type LocalMenuGroupToSiteRelationship,
  P2Role,
  type DeliveryDestinationResponse,
  type StationBrand,
  type SiteLocation,
  CountryCode,
  type Multigroup,
  type SitesUsingPriceLevelResponse,
  type MenuHoursStationDetail,
  type SiteMenuHoursDetailsResponse,
} from '@centric-os/types';
import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { defineStore, storeToRefs, type StateTree } from 'pinia';
import { useAuthStore } from '@centric-os/stores/auth/use-auth-store';
import { isEmpty } from 'lodash-es';
import { useKDSStore } from '@centric-os/stores/sites/use-kds-store';
import { useMultigroupStore } from '@centric-os/stores/sites/use-multigroup-store';

interface State extends StateTree {
  site: Site;
  sites: Site[];
  siteDeliveryDestinations: DeliveryDestination[];
  updateSiteOnNextMount: boolean;
  siteLocalMenuGroups: LocalMenuGroupToSiteRelationship[];
  sitesUsingPriceLevel: SitesUsingPriceLevelResponse['sites'];
  // Per-station Menu Hours details, keyed by station id (lazily loaded from the edge).
  menuHoursDetails: Record<string, MenuHoursStationDetail>;
  // should be temporaly
  loadingAllSites: boolean;
  editSite: boolean;
}

const DEFAULT_SITES_QUERY_PARAMS: CDLQueryParams = {
  sortBy: 'created_at',
  sortOrder: 'DESC',
};

type SitesListInFlightState = {
  abortController: AbortController | null;
  inFlight: { key: string; promise: Promise<void> } | null;
};

type SitesListStoreContext = {
  cdlApi: { get: typeof axios.get };
  pagination: { queryParams: CDLQueryParams };
  sites: Site[];
  // @ts-expect-error FIXME: type assignment error (TS2344)
  updatePaginationWithMeta: (meta: RecordList<Site>['meta']) => void;
};

async function resolveSitesListParams(
  store: Pick<SitesListStoreContext, 'pagination'>,
  noPagination: boolean,
  params: CDLQueryParams | undefined,
  defaultParams: CDLQueryParams,
): Promise<CDLQueryParams> {
  const authStore = useAuthStore();
  const multigroupStore = useMultigroupStore();

  const isSiteOperator = authStore.hasRole(P2Role.SITE_OPERATOR);

  if (!isSiteOperator) {
    await multigroupStore.getMultiGroups();
  }

  let resolvedParams = params ?? defaultParams;

  if (!noPagination) {
    resolvedParams = store.pagination.queryParams;
  }

  if (
    !isEmpty(multigroupStore.getMultigroupIDs) &&
    !isEmpty(resolvedParams) &&
    isEmpty(resolvedParams.id_multigroups)
  ) {
    resolvedParams = { ...resolvedParams, id_multigroups: multigroupStore.getMultigroupIDs };
  }

  return resolvedParams;
}

async function fetchSitesList(
  store: SitesListStoreContext,
  inFlightState: SitesListInFlightState,
  options: {
    noPagination: boolean;
    params?: CDLQueryParams;
    config?: AxiosRequestConfig;
    defaultParams: CDLQueryParams;
    endpoint: string;
    mapResults: (results: Array<Site | Pick<Site, 'id' | 'name'>>) => Site[];
  },
): Promise<void> {
  const resolvedParams = await resolveSitesListParams(
    store,
    options.noPagination,
    options.params,
    options.defaultParams,
  );
  const resolvedConfig = options.config ?? {};
  const key = `${options.noPagination}:${JSON.stringify(resolvedParams)}`;

  if (inFlightState.inFlight?.key === key) {
    return inFlightState.inFlight.promise;
  }

  inFlightState.abortController?.abort();
  inFlightState.abortController = new AbortController();
  const { signal } = inFlightState.abortController;

  const promise = (async () => {
    try {
      const response = await store.cdlApi.get<
        // @ts-expect-error FIXME: type assignment error (TS2344)
        RecordList<Site | Pick<Site, 'id' | 'name'>>
      >(options.endpoint, {
        ...resolvedConfig,
        signal,
        params: resolvedParams,
      });
      const { results, meta } = response.data;

      store.sites = options.mapResults(results);

      if (!options.noPagination) {
        store.updatePaginationWithMeta(meta);
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        return;
      }
      throw err;
    }
  })().finally(() => {
    if (inFlightState.inFlight?.key === key) {
      inFlightState.inFlight = null;
    }
  });

  inFlightState.inFlight = { key, promise };
  return promise;
}

export function useSiteStore(storeId = 'site') {
  const getSitesInFlightState: SitesListInFlightState = {
    abortController: null,
    inFlight: null,
  };
  const getDiscountSitesInFlightState: SitesListInFlightState = {
    abortController: null,
    inFlight: null,
  };

  const store = defineStore(storeId, {
    state: (): State => ({
      site: null,
      sites: [],
      siteDeliveryDestinations: [],
      updateSiteOnNextMount: false,
      siteLocalMenuGroups: [],
      sitesUsingPriceLevel: [],
      menuHoursDetails: {},
      // should be temporaly
      loadingAllSites: false,
      editSite: false,
    }),
    actions: {
      async getSites(
        noPagination: boolean = false,
        params: CDLQueryParams = {
          sortBy: 'created_at',
          sortOrder: 'DESC',
        },
        config: AxiosRequestConfig = {},
      ): Promise<void> {
        // @ts-expect-error FIXME: argument type mismatch (TS2345)
        return fetchSitesList(this, getSitesInFlightState, {
          noPagination,
          params,
          config,
          defaultParams: DEFAULT_SITES_QUERY_PARAMS,
          endpoint: '/centricos/sites',
          mapResults: (results) => results as Site[],
        });
      },
      async getDiscountSites(
        noPagination: boolean = false,
        params?: CDLQueryParams,
        config?: AxiosRequestConfig,
      ): Promise<void> {
        // @ts-expect-error FIXME: argument type mismatch (TS2345)
        return fetchSitesList(this, getDiscountSitesInFlightState, {
          noPagination,
          params,
          config,
          defaultParams: DEFAULT_SITES_QUERY_PARAMS,
          endpoint: '/centricos/discounts/sites',
          mapResults: (results) => results as Site[],
        });
      },
      resetAndClearFilter(): void {
        // Resets and clears the site search query
        this.$reset();
        delete this.pagination.queryParams.filter;
      },
      async getSite(siteId: RecordEntityId, extended = true): Promise<void> {
        const response = await this.cdlApi.get<Site>(`/location/group/${siteId}?nocache=1`, {
          params: { extended: extended, include_estimated_wait_time: false },
        });
        this.site = response.data;
      },
      async getSiteDetailsPage(siteId: RecordEntityId, extended = true, includeDeleted = false): Promise<void> {
        await this.cdlApi
          .get<Site>(`/location/group/${siteId}?nocache=1`, {
            params: { extended: extended, include_estimated_wait_time: false, include_deleted: includeDeleted },
          })
          .then(async (response) => {
            this.site = response.data;

            // Fetch KDS devices for the site BUs
            const kdsStore = useKDSStore();
            await kdsStore.fetchKDSDevices();

            // Fetch delivery destinations for the site
            await Promise.all([
              this.getSiteDeliveryDestinations(siteId, includeDeleted),
              this.getSitesLocalMenuGroups(siteId),
            ]);
          })
          .catch((err) => {
            if (err?.status === 401) {
              useAuthStore().logout();
            }
            throw err;
          });
      },
      /**
       * Lazily loads per-station Menu Hours details (the "Show in App" toggle gate and state)
       * from the centricos edge and stores them keyed by station id.
       */
      async getMenuHoursDetails(siteId: RecordEntityId): Promise<void> {
        const response = await this.cdlApi.get<SiteMenuHoursDetailsResponse>(
          `/centricos/site/${siteId}/menu-hours-details`,
        );
        this.menuHoursDetails = Object.fromEntries(
          (response.data?.stations ?? []).map((station) => [station.id, station]),
        );
      },
      async getSiteNoExtended(siteId: RecordEntityId): Promise<Site> {
        const response = await this.cdlApi.get<Site>(
          `/location/group/${siteId}?nocache=1&include_estimated_wait_time=false`,
        );
        return response?.data;
      },
      async getSitesByIds(siteIds: RecordEntityId[]): Promise<Site[]> {
        if (siteIds.length === 0) return [];
        const uniqueIds = Array.from(new Set(siteIds));
        const batchSize = 50;
        const requests: Promise<AxiosResponse<{ groups?: Site[] }>>[] = [];
        for (let index = 0; index < uniqueIds.length; index += batchSize) {
          requests.push(
            this.cdlApi.post<{ groups?: Site[] }>('/location/group/batch-get', {
              ids: uniqueIds.slice(index, index + batchSize),
            }),
          );
        }
        const responses = await Promise.allSettled(requests);
        return responses.flatMap((response) =>
          response.status === 'fulfilled' ? response.value.data.groups ?? [] : [],
        );
      },
      async updateSite(siteId: RecordEntityId, payload: Partial<Site>): Promise<void> {
        const response = await this.cdlApi.patch<Site>(`/location/group/${siteId}`, payload);
        this.site = response.data;
      },
      async updateLocationSequence(
        id: RecordEntityId,
        payload: Partial<SiteLocation>,
      ): Promise<void> {
        const response = await this.cdlApi.patch<SiteLocation>(`/location/${id}`, payload);
        const updatedLocation = response.data;
        const index = this.site.locations.findIndex(
          (location) => location.id === updatedLocation.id,
        );
        if (index !== -1) {
          this.site.locations[index] = updatedLocation;
        }
      },
      async getSiteDeliveryDestinations(siteId: RecordEntityId, includeDeleted = false): Promise<void> {
        const response = await this.cdlApi.get<DeliveryDestinationResponse>(
          `/location/group/${siteId}/deliverydestination`,
          { params: { include_deleted: includeDeleted } },
        );

        // Sort the delivery destinations by sort_sequence without mutating the original array
        const sortedLocations = response?.data?.delivery_destinations?.toSorted(
          (a: DeliveryDestination, b: DeliveryDestination) => a.sort_sequence - b.sort_sequence,
        );

        this.siteDeliveryDestinations = sortedLocations;
      },
      async getSitesLocalMenuGroups(
        siteId: RecordEntityId,
      ): Promise<LocalMenuGroupToSiteRelationship[]> {
        const response = await this.cdlApi.get<RecordList<LocalMenuGroupToSiteRelationship>>(
          `/menu/v3/local-menu-group/${siteId}/site`,
        );
        this.siteLocalMenuGroups = response?.data?.results;
        return this.siteLocalMenuGroups;
      },
      //  This method should be temporaly
      async getSitesForUser(): Promise<void> {
        this.loadingAllSites = true;
        // Stores for user config
        const authStore = useAuthStore();
        const { cdlUser } = storeToRefs(authStore);
        const multigroupStore = useMultigroupStore();
        const { multigroups } = storeToRefs(multigroupStore);

        // Fetch multigroups if user is not site operator
        if (isEmpty(multigroups.value)) {
          await multigroupStore.getMultiGroups();
        }

        const promises = multigroups.value?.map((multigroup) =>
          this.getUserMultigroupSites(multigroup.id, cdlUser.value?.id),
        );

        // Wait for all promises to resolve
        const results = await Promise.all(promises);

        // Flatten the array of arrays into a single array
        const flattenedResultsArray = results.flat().filter((result) => result !== null);

        this.sites = flattenedResultsArray;
        this.loadingAllSites = false;
      },
      // This method should be temporaly
      async getUserMultigroupSites(multigroupId: string, userId: string): Promise<Site[]> {
        const response = await this.cdlApi.get<any>(`/location/multigroup/${multigroupId}`);

        return response.data?.groups;
      },

      /**
       * @returns true if the site is Canadian, false otherwise
       */
      isCanadianSite(): boolean {
        return this.site?.address?.country?.toLowerCase() === CountryCode.CA.toLowerCase();
      },
      async createSite(payload: Partial<Site>): Promise<Site> {
        const response = await this.cdlApi.post<Site>('/location/group', payload);
        this.site = response.data;
        return this.site;
      },
      async createSiteViaEdge(payload: Partial<Site>): Promise<Site> {
        const response = await this.cdlApi.post<Site>('/centricos/site', payload);
        this.site = response.data;
        return this.site;
      },
      async addSiteToMultigroup(payload: Partial<Multigroup>): Promise<void> {
        await this.cdlApi.patch(`/location/multigroup/${payload.id}`, payload);
      },
      async getSitesForPriceLevel(priceLevelId: RecordEntityId): Promise<void> {
        const response = await this.cdlApi.get<SitesUsingPriceLevelResponse>(
          `/centricos/sites/price-level/${priceLevelId}`,
        );
        this.sitesUsingPriceLevel = response.data?.sites;
      },
      async updateBulkBrands(payload: Partial<StationBrand>) {
        if (payload) {
          await this.cdlApi.patch<
            StationBrand,
            AxiosResponse<StationBrand[], any>,
            Partial<StationBrand>
          >(`/location/brand/bulk`, {
            ...payload,
          });
        } else {
          return Promise.reject(new Error('no payload selected'));
        }
      },

      setSiteStations(payload: { locationId: string; brands: StationBrand[] }) {
        if (payload) {
          const location = this.site.locations.filter((loc) => loc.id === payload.locationId)[0];
          location.brands = payload.brands;
        }
      },

      async deleteSite(siteId: RecordEntityId): Promise<void> {
        await this.cdlApi.delete(`/location/v2/group/${siteId}`);
      },
      async restoreSite(siteId: RecordEntityId): Promise<void> {
        await this.cdlApi.post(`/location/group/${siteId}/restore`);
      },
    },
    getters: {
      sitesStations: (state: State): StationBrand[] => {
        return (
          state.site?.locations?.flatMap((location) => {
            return location.brands;
          }) || []
        );
      },
      sitesLocationMap: (state: State): Record<RecordEntityId, SiteLocation> => {
        return state.site?.locations?.reduce(
          (acc: Record<RecordEntityId, SiteLocation>, val: SiteLocation) => {
            acc[val.id] = val;
            return acc;
          },
          {},
        );
      },
      isThriveSite: (state: State): boolean => {
        return state.site?.multigroup === import.meta.env.VITE_THRIVE_MULTIGROUP;
      },
      isBoostSite: (state: State): boolean => {
        return state.site?.multigroup === import.meta.env.VITE_BOOST_MULTIGROUP;
      },
      isThriveBoostSite(): boolean {
        return this.isThriveSite || this.isBoostSite;
      },
      isTacitSite: (state: State): boolean => {
        return state.site?.multigroup === import.meta.env.VITE_TACIT_MULTIGROUP;
      },
      isUnlistedSite: (state: State): boolean =>
        state.site?.locations?.[0]?.location_multigroup ===
        import.meta.env.VITE_UNLISTED_MULTIGROUP,
    },
    pagination: true,
    paginationConfig: {
      ...defaultSitePaginationConfig,
    },
  });
  return store();
}
